import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";

export const fetchBrands = createAsyncThunk(
  "brand/fetchBrands",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null) return;

          // If filterValue is an object (like from_date/to_date)
          if (typeof filterValue === "object" && filterValue !== null) {

            if (filterField === "created_at") {
              params.append(`created_at_from`, filterValue.from_date);
              params.append(`created_at_to`, filterValue.to_date);
            } else if (filterField === "updated_at") {
              params.append(`updated_at_from`, filterValue.from_date);
              params.append(`updated_at_to`, filterValue.to_date);
            }
            else {

              if (filterValue.from_date)
                params.append(`${filterField}_date_from`, filterValue.from_date);
              if (filterValue.to_date)
                params.append(`${filterField}_date_to`, filterValue.to_date);
            }
          } else {
            // Primitive value
            params.append(filterField, String(filterValue));
          }
        }
      );
    }

    const response: any = await Axios.get(`api/brand/list/?${params.toString()}`);

    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const fetchBrandById = createAsyncThunk(
  "brand/fetchBrandById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(`api/brand/details/${id}/`);
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch brand"
      );
    }
  }
);

export const updateBrand = createAsyncThunk(
  "brand/updateBrand",
  async (data: any, { rejectWithValue }) => {
    try {
      const { id, ...payload } = data;
      const response = await Axios.put(`api/brand/update/${id}/`, payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update brand");
    }
  }
);

export const addBrand = createAsyncThunk(
  "brand/addBrand",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(`api/brand/create/`, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add brand");
    }
  }
);

export const exportBrandCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  params.append("export", "csv");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (!filterField || filterValue === undefined || filterValue === null) return;

        // If filterValue is an object (like from_date/to_date)
        if (typeof filterValue === "object" && filterValue !== null) {

          if (filterField === "created_at") {
            params.append(`created_at_from`, filterValue.from_date);
            params.append(`created_at_to`, filterValue.to_date);
          } else if (filterField === "updated_at") {
            params.append(`updated_at_from`, filterValue.from_date);
            params.append(`updated_at_to`, filterValue.to_date);
          }
          else {

            if (filterValue.from_date)
              params.append(`${filterField}_date_from`, filterValue.from_date);
            if (filterValue.to_date)
              params.append(`${filterField}_date_to`, filterValue.to_date);
          }
        } else {
          // Primitive value
          params.append(filterField, String(filterValue));
        }
      }
    );
  }

  const response = await Axios.get(`api/brand/list/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "brand.csv",
  };
};

